import React from 'react';

import {
    BLOCK_CODE,
    BLOCK_COLUMNS,
    BLOCK_EXPANDABLE,
    BLOCK_HEADING_1,
    BLOCK_HEADING_2,
    BLOCK_HEADING_3,
    BLOCK_HINT,
    BLOCK_LIST_OL,
    BLOCK_LIST_TASKS,
    BLOCK_LIST_UL,
    BLOCK_PARAGRAPH,
    BLOCK_QUOTE,
    BLOCK_STEPPER,
    BLOCK_TABS,
    canInsertBlockIn,
    expandable,
    tabs,
} from '@gitbook/doc-core';
import { createCodeBlock } from '@gitbook/doc-core/src/code/utils';
import { createColumnsBlock } from '@gitbook/doc-core/src/columns';
import { createHint } from '@gitbook/doc-core/src/hint/utils';
import { createList } from '@gitbook/doc-core/src/list';
import { createParagraph } from '@gitbook/doc-core/src/paragraph';
import { createStepper } from '@gitbook/doc-core/src/stepper';
import {
    createPalette,
    type PaletteCommandDef,
    PaletteCommandType,
    type PaletteGroupDef,
    PalettePositionMaxHeight,
    PalettePositionWidth,
} from '@gitbook/react-uikit';
import { Block, type Change, generateKey, Inline } from '@gitbook/slate';
import { useEditor } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { assertNever, filterOutNullable } from '@gitbook/util-ts';

import { ContentEditorContext } from '../contexts';
import { useReusableContentPaletteGroup } from '../reusable-content';
import { useBlockPaletteFactory } from './blockPalette';

/**
 * Hook to use a commands palette definition to edit a text block (paragraph, headings, etc).
 */
export function useTextBlockPaletteFactory(
    nodeKey: string,
    options?: { extraGroups: PaletteGroupDef[] }
) {
    const editor = useEditor();
    const { logAnalyticsEvent } = React.useContext(ContentEditorContext);
    const createReusableContentGroup = useReusableContentPaletteGroup({
        nodeKey,
        method: 'editor-palette',
        title: 'Reusable content',
    });

    function handleBlockTypeChange(change: Change, block: Block, target: BlockType) {
        logAnalyticsEvent({
            type: 'edit_page_document_edit_block_type',
            fromType: block.type,
            toType: target,
        });

        paragraphToBlock(change, block, target);
    }

    const createExtraGroups = useEventCallback(() => {
        const extraGroups = options?.extraGroups || [];
        const groups: PaletteGroupDef[] = [];
        const reusableContentGroup = createReusableContentGroup();
        const reusableContentCommands = reusableContentGroup?.commands || [];

        groups.push({
            key: 'turn_into',
            title: 'Turn into',
            commands: () => {
                const block = editor.value.document.assertBlock(nodeKey);
                const parent = editor.value.document.getParent(nodeKey);

                if (!parent) {
                    return [];
                }

                return [
                    canInsertBlockIn(parent, BLOCK_HEADING_1)
                        ? ({
                              key: BLOCK_HEADING_1,
                              title: 'Heading 1',
                              type: PaletteCommandType.RADIO,
                              selected: block.type === BLOCK_HEADING_1,
                              icon: Icon.Heading1,
                              trigger: () => {
                                  editor.change((c) =>
                                      handleBlockTypeChange(c, block, BLOCK_HEADING_1)
                                  );
                              },
                          } satisfies PaletteCommandDef)
                        : null,
                    canInsertBlockIn(parent, BLOCK_HEADING_2)
                        ? ({
                              key: BLOCK_HEADING_2,
                              title: 'Heading 2',
                              type: PaletteCommandType.RADIO,
                              selected: block.type === BLOCK_HEADING_2,
                              icon: Icon.Heading2,
                              trigger: () => {
                                  editor.change((c) =>
                                      handleBlockTypeChange(c, block, BLOCK_HEADING_2)
                                  );
                              },
                          } satisfies PaletteCommandDef)
                        : null,
                    canInsertBlockIn(parent, BLOCK_HEADING_3)
                        ? ({
                              key: BLOCK_HEADING_3,
                              title: 'Heading 3',
                              type: PaletteCommandType.RADIO,
                              selected: block.type === BLOCK_HEADING_3,
                              icon: Icon.Heading3,
                              trigger: () => {
                                  editor.change((c) =>
                                      handleBlockTypeChange(c, block, BLOCK_HEADING_3)
                                  );
                              },
                          } satisfies PaletteCommandDef)
                        : null,
                    {
                        key: 'turn_into_more',
                        title: 'More blocks',
                        icon: Icon.Replace,
                        descendant: () => {
                            return createPalette({
                                search: { placeholder: 'Search blocks...' },
                                position: {
                                    width: PalettePositionWidth.ANCHOR,
                                },
                                groups: [
                                    reusableContentCommands.length > 0
                                        ? {
                                              key: 'reusable_content',
                                              commands: reusableContentCommands,
                                          }
                                        : null,
                                    {
                                        key: 'block_types',
                                        title: 'Blocks',
                                        commands: [
                                            {
                                                key: BLOCK_PARAGRAPH,
                                                title: 'Paragraph',
                                                type: PaletteCommandType.RADIO,
                                                selected: block.type === BLOCK_PARAGRAPH,
                                                icon: Icon.Paragraph,
                                                trigger: () => {
                                                    editor.change((c) =>
                                                        paragraphToBlock(c, block, BLOCK_PARAGRAPH)
                                                    );
                                                },
                                            } satisfies PaletteCommandDef,
                                            canInsertBlockIn(parent, BLOCK_HINT)
                                                ? ({
                                                      key: BLOCK_HINT,
                                                      title: 'Hint',
                                                      type: PaletteCommandType.RADIO,
                                                      selected: block.type === BLOCK_HINT,
                                                      icon: Icon.InfoCircle,
                                                      trigger: () => {
                                                          editor.change((c) =>
                                                              handleBlockTypeChange(
                                                                  c,
                                                                  block,
                                                                  BLOCK_HINT
                                                              )
                                                          );
                                                      },
                                                  } satisfies PaletteCommandDef)
                                                : null,
                                            canInsertBlockIn(parent, BLOCK_LIST_UL)
                                                ? ({
                                                      key: BLOCK_LIST_UL,
                                                      title: 'Unordered List',
                                                      type: PaletteCommandType.RADIO,
                                                      selected: block.type === BLOCK_LIST_UL,
                                                      icon: Icon.ListUnordered,
                                                      trigger: () => {
                                                          editor.change((c) =>
                                                              handleBlockTypeChange(
                                                                  c,
                                                                  block,
                                                                  BLOCK_LIST_UL
                                                              )
                                                          );
                                                      },
                                                  } satisfies PaletteCommandDef)
                                                : null,
                                            canInsertBlockIn(parent, BLOCK_LIST_OL)
                                                ? ({
                                                      key: BLOCK_LIST_OL,
                                                      title: 'Ordered List',
                                                      type: PaletteCommandType.RADIO,
                                                      selected: block.type === BLOCK_LIST_OL,
                                                      icon: Icon.ListOrdered,
                                                      trigger: () => {
                                                          editor.change((c) =>
                                                              handleBlockTypeChange(
                                                                  c,
                                                                  block,
                                                                  BLOCK_LIST_OL
                                                              )
                                                          );
                                                      },
                                                  } satisfies PaletteCommandDef)
                                                : null,
                                            canInsertBlockIn(parent, BLOCK_LIST_TASKS)
                                                ? ({
                                                      key: BLOCK_LIST_TASKS,
                                                      title: 'Task List',
                                                      type: PaletteCommandType.RADIO,
                                                      selected: block.type === BLOCK_LIST_TASKS,
                                                      icon: Icon.ListTasks,
                                                      trigger: () => {
                                                          editor.change((c) =>
                                                              handleBlockTypeChange(
                                                                  c,
                                                                  block,
                                                                  BLOCK_LIST_TASKS
                                                              )
                                                          );
                                                      },
                                                  } satisfies PaletteCommandDef)
                                                : null,
                                            canInsertBlockIn(parent, BLOCK_CODE)
                                                ? ({
                                                      key: BLOCK_CODE,
                                                      title: 'Code block',
                                                      type: PaletteCommandType.RADIO,
                                                      selected: block.type === BLOCK_CODE,
                                                      icon: Icon.Code,
                                                      trigger: () => {
                                                          editor.change((c) =>
                                                              handleBlockTypeChange(
                                                                  c,
                                                                  block,
                                                                  BLOCK_CODE
                                                              )
                                                          );
                                                      },
                                                  } satisfies PaletteCommandDef)
                                                : null,
                                            canInsertBlockIn(parent, BLOCK_QUOTE)
                                                ? ({
                                                      key: BLOCK_QUOTE,
                                                      title: 'Quote',
                                                      type: PaletteCommandType.RADIO,
                                                      selected: block.type === BLOCK_QUOTE,
                                                      icon: Icon.Quote,
                                                      trigger: () => {
                                                          editor.change((c) =>
                                                              handleBlockTypeChange(
                                                                  c,
                                                                  block,
                                                                  BLOCK_QUOTE
                                                              )
                                                          );
                                                      },
                                                  } satisfies PaletteCommandDef)
                                                : null,
                                            canInsertBlockIn(parent, BLOCK_TABS)
                                                ? ({
                                                      key: BLOCK_TABS,
                                                      title: 'Tabs',
                                                      type: PaletteCommandType.RADIO,
                                                      selected: block.type === BLOCK_TABS,
                                                      icon: Icon.Tabs,
                                                      trigger: () => {
                                                          editor.change((c) =>
                                                              handleBlockTypeChange(
                                                                  c,
                                                                  block,
                                                                  BLOCK_TABS
                                                              )
                                                          );
                                                      },
                                                  } satisfies PaletteCommandDef)
                                                : null,
                                            canInsertBlockIn(parent, BLOCK_STEPPER)
                                                ? ({
                                                      key: BLOCK_STEPPER,
                                                      title: 'Stepper',
                                                      type: PaletteCommandType.RADIO,
                                                      selected: block.type === BLOCK_STEPPER,
                                                      icon: Icon.Stepper,
                                                      trigger: () => {
                                                          editor.change((c) =>
                                                              handleBlockTypeChange(
                                                                  c,
                                                                  block,
                                                                  BLOCK_STEPPER
                                                              )
                                                          );
                                                      },
                                                  } satisfies PaletteCommandDef)
                                                : null,
                                            canInsertBlockIn(parent, BLOCK_EXPANDABLE)
                                                ? ({
                                                      key: BLOCK_EXPANDABLE,
                                                      title: 'Expandable',
                                                      type: PaletteCommandType.RADIO,
                                                      selected: block.type === BLOCK_EXPANDABLE,
                                                      icon: Icon.BlockExpandable,
                                                      trigger: () => {
                                                          editor.change((c) =>
                                                              handleBlockTypeChange(
                                                                  c,
                                                                  block,
                                                                  BLOCK_EXPANDABLE
                                                              )
                                                          );
                                                      },
                                                  } satisfies PaletteCommandDef)
                                                : null,
                                            canInsertBlockIn(parent, BLOCK_COLUMNS)
                                                ? ({
                                                      key: BLOCK_COLUMNS,
                                                      title: 'Columns',
                                                      type: PaletteCommandType.RADIO,
                                                      selected: block.type === BLOCK_COLUMNS,
                                                      icon: Icon.Columns,
                                                      trigger: () => {
                                                          editor.change((c) =>
                                                              handleBlockTypeChange(
                                                                  c,
                                                                  block,
                                                                  BLOCK_COLUMNS
                                                              )
                                                          );
                                                      },
                                                  } satisfies PaletteCommandDef)
                                                : null,
                                        ].filter(filterOutNullable),
                                    },
                                ].filter(filterOutNullable),
                            });
                        },
                    },
                ].filter(filterOutNullable);
            },
        });
        groups.push({
            key: 'align',
            commands: () => {
                const block = editor.value.document.assertBlock(nodeKey);
                const align = block.data.get('align');
                const changeAlign = (value: 'start' | 'center' | 'end') => {
                    editor.change((c) => {
                        c.insertDataByKey(block.key, ['align'], value);
                    });
                };
                return [
                    {
                        key: 'start',
                        type: PaletteCommandType.CHECKBOX,
                        title: 'Left align',
                        selected: align === 'start' || typeof align === 'undefined',
                        icon: Icon.AlignTextLeft,
                        trigger: () => changeAlign('start'),
                    } satisfies PaletteCommandDef,
                    {
                        key: 'center',
                        type: PaletteCommandType.CHECKBOX,
                        title: 'Center',
                        selected: align === 'center',
                        icon: Icon.AlignTextCenter,
                        trigger: () => changeAlign('center'),
                    } satisfies PaletteCommandDef,
                    {
                        key: 'end',
                        type: PaletteCommandType.CHECKBOX,
                        title: 'Right align',
                        selected: align === 'end',
                        icon: Icon.AlignTextRight,
                        trigger: () => changeAlign('end'),
                    } satisfies PaletteCommandDef,
                ];
            },
        });
        groups.push(...extraGroups);
        return groups;
    });

    return useBlockPaletteFactory({
        nodeKey,
        aiCompletion: true,
        addReusableContentGroup: false, // Disable top-level reusable content group
        addConditionalContentGroup: false,
        createExtraGroups,
        position: {
            maxHeight: PalettePositionMaxHeight.AUTO,
        },
    });
}

export type BlockType =
    | typeof BLOCK_PARAGRAPH
    | typeof BLOCK_HEADING_1
    | typeof BLOCK_HEADING_2
    | typeof BLOCK_HEADING_3
    | typeof BLOCK_HINT
    | typeof BLOCK_QUOTE
    | typeof BLOCK_LIST_UL
    | typeof BLOCK_LIST_OL
    | typeof BLOCK_LIST_TASKS
    | typeof BLOCK_CODE
    | typeof BLOCK_EXPANDABLE
    | typeof BLOCK_STEPPER
    | typeof BLOCK_TABS
    | typeof BLOCK_COLUMNS;

/**
 * Clone the nodes of the paragraph so we preserve any marks and rich formatting instead of just passing the raw text.
 */
const cloneParagraphNodes = (p: Block) =>
    p.nodes.toArray().map((n) => n.merge({ key: generateKey() }));

/**
 * Convert a paragraph → any other block type that the palette supports.
 * Decides between:
 *   - setNodeByKey (simply switch the block type, for blocks that share structure with paragraphs)
 *   – wrapBlockByKey (for simple containers: hint, quote etc)
 *   – replaceNodeByKey (for complex structures: lists, tabs, expandable etc)
 */
export function paragraphToBlock(change: Change, paragraph: Block, target: BlockType) {
    const keep = cloneParagraphNodes(paragraph);

    switch (target) {
        case BLOCK_PARAGRAPH:
        case BLOCK_HEADING_1:
        case BLOCK_HEADING_2:
        case BLOCK_HEADING_3: {
            change.setNodeByKey(paragraph.key, { type: target });
            break;
        }

        case BLOCK_HINT: {
            change.wrapBlockByKey(paragraph.key, {
                type: BLOCK_HINT,
                data: createHint().data,
            });
            break;
        }

        case BLOCK_LIST_UL:
        case BLOCK_LIST_OL:
        case BLOCK_LIST_TASKS: {
            const list = createList(target, keep);
            change.replaceNodeByKey(paragraph.key, list);
            break;
        }

        case BLOCK_CODE: {
            const code = createCodeBlock(keep);
            change.replaceNodeByKey(paragraph.key, code);
            break;
        }

        case BLOCK_QUOTE: {
            change.wrapBlockByKey(paragraph.key, { type: BLOCK_QUOTE });
            break;
        }

        case BLOCK_TABS: {
            const body = Block.create({
                type: BLOCK_PARAGRAPH,
                nodes: keep,
            });
            const tabsBlock = tabs.createTabs([
                tabs.createTabsItem('Tab 1', [body]),
                tabs.createTabsItem('Tab 2'),
            ]);
            change.replaceNodeByKey(paragraph.key, tabsBlock);
            break;
        }

        case BLOCK_STEPPER: {
            const inline = Inline.create({ nodes: cloneParagraphNodes(paragraph) });
            const stepper = createStepper([inline]);
            change.replaceNodeByKey(paragraph.key, stepper);
            break;
        }

        case BLOCK_EXPANDABLE: {
            const exp = expandable.createExpandableBlock(keep);
            change.replaceNodeByKey(paragraph.key, exp);
            break;
        }

        case BLOCK_COLUMNS: {
            const columns = createColumnsBlock([[createParagraph(keep)], [createParagraph()]]);
            change.replaceNodeByKey(paragraph.key, columns);
            break;
        }

        default:
            assertNever(target);
    }
}
